import React from 'react';
import { MediaLibraryItem } from '../../../api/media';
import { constructMediaUrl } from '../../../api/helpers';
import { resolveMediaPreviewCandidates } from '../mediaPreview';
import { shouldRetryVideoError } from '../../clips/videoError';
import VideoThumbnail from './VideoThumbnail';

interface BrowseTabPreviewProps {
  item: MediaLibraryItem;
  /**
   * `row` — 44px thumbnail. `detail` — capped 11rem preview (legacy sidebar).
   * `hero` — the inspector's dominant preview: fills the available space with
   * the full-resolution original, no small height cap.
   */
  mode: 'row' | 'detail' | 'hero';
}

const basePlaceholder =
  'rounded border border-white/20 bg-black/50 flex items-center justify-center text-[10px] text-zinc-400 uppercase';

const renderNoPreview = (
  folder: string,
  mode: 'row' | 'detail' | 'hero',
  detailMessage = 'No preview URL available for this file.'
) =>
  mode === 'row' ? (
    <div className={`w-11 h-11 ${basePlaceholder}`}>{folder}</div>
  ) : (
    <p className="attachment-meta">{detailMessage}</p>
  );

export const BrowseTabPreview: React.FC<BrowseTabPreviewProps> = ({ item, mode }) => {
  // `detail` and `hero` are both "large" previews; only their sizing differs.
  const isHero = mode === 'hero';
  const imageClass = isHero
    ? 'media-hero-img'
    : 'w-full max-h-44 object-contain rounded border border-white/15 bg-black/40';
  const videoClass = isHero
    ? 'media-hero-video'
    : 'w-full max-h-44 rounded border border-white/15 bg-black/40';
  const posterClass = isHero
    ? 'media-hero-img'
    : 'w-full max-h-44 object-contain rounded border border-white/15 bg-black/40';
  const [showOriginal, setShowOriginal] = React.useState(false);

  const itemKey = item.media_id || item.id;
  React.useEffect(() => { setShowOriginal(false); }, [itemKey, mode]);

  const isLipSynced = !!(item.metadata?.lip_synced);
  const originalUrl = item.metadata?.original_url as string | undefined;

  // When showing original, override the item's video/url fields
  const resolvedItem = showOriginal && originalUrl
    ? { ...item, url: originalUrl, file_url: originalUrl, preview_url: undefined, thumbnail_url: undefined }
    : item;

  const { folder, image, video, audio, poster } = resolveMediaPreviewCandidates(resolvedItem);
  const [failedUrlSet, setFailedUrlSet] = React.useState<Set<string>>(new Set());

  React.useEffect(() => {
    setFailedUrlSet(new Set());
  }, [item.media_id, item.id, mode]);

  const handleCandidateError = (url: string) => {
    setFailedUrlSet((prev) => {
      const next = new Set(prev);
      next.add(url);
      return next;
    });
  };

  // Bounded, cache-buster-free retries for the large `<video>` preview: Firefox
  // emits transient network/abort errors on metadata loads, and marking the
  // candidate failed on the first one shows a false "preview unavailable". Only
  // give up (fall to the fallback text) once the error is genuinely fatal or the
  // retry budget is spent.
  const videoElRef = React.useRef<HTMLVideoElement>(null);
  const [videoRetries, setVideoRetries] = React.useState(0);
  React.useEffect(() => {
    setVideoRetries(0);
  }, [item.media_id, item.id, mode, showOriginal]);

  const handleVideoError = (url: string) => {
    const code = videoElRef.current?.error?.code;
    if (shouldRetryVideoError(code, videoRetries)) {
      setVideoRetries((n) => n + 1);
      return;
    }
    handleCandidateError(url);
  };

  const syncedToggle = mode !== 'row' && isLipSynced && originalUrl ? (
    <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
      <button
        onClick={() => setShowOriginal(false)}
        style={{
          padding: '3px 8px', fontSize: '11px', borderRadius: '4px', border: 'none',
          cursor: 'pointer',
          background: !showOriginal ? '#7c6aff' : '#222',
          color: !showOriginal ? '#fff' : '#888',
        }}
      >
        Synced
      </button>
      <button
        onClick={() => setShowOriginal(true)}
        style={{
          padding: '3px 8px', fontSize: '11px', borderRadius: '4px', border: 'none',
          cursor: 'pointer',
          background: showOriginal ? '#7c6aff' : '#222',
          color: showOriginal ? '#fff' : '#888',
        }}
      >
        Original
      </button>
    </div>
  ) : null;

  const imageUrl = image.find((candidate) => !failedUrlSet.has(candidate)) || '';
  const videoUrl = video.find((candidate) => !failedUrlSet.has(candidate)) || '';
  const audioUrl = audio.find((candidate) => !failedUrlSet.has(candidate)) || '';
  const posterUrl = [...poster, ...image].find((candidate) => !failedUrlSet.has(candidate)) || '';
  const sourceFileUrl = video[0] || item.url || '';
  const resolvedImageUrl = imageUrl ? constructMediaUrl(imageUrl) : '';
  const resolvedVideoUrl = videoUrl ? constructMediaUrl(videoUrl) : '';
  const resolvedAudioUrl = audioUrl ? constructMediaUrl(audioUrl) : '';
  const resolvedPosterUrl = posterUrl ? constructMediaUrl(posterUrl) : '';
  const resolvedSourceFileUrl = sourceFileUrl ? constructMediaUrl(sourceFileUrl) : '';

  if (folder === 'image') {
    if (!imageUrl) return renderNoPreview(folder, mode);
    if (mode !== 'row') {
      return (
        <>
          {syncedToggle}
          <img
            src={resolvedImageUrl}
            alt={item.name || item.media_id || item.id}
            className={imageClass}
            loading="lazy"
            onError={() => handleCandidateError(imageUrl)}
          />
        </>
      );
    }
    return (
      <img
        src={resolvedImageUrl}
        alt={item.name || item.media_id || item.id}
        className="w-11 h-11 rounded object-cover border border-white/20"
        loading="lazy"
        onError={() => handleCandidateError(imageUrl)}
      />
    );
  }

  if (folder === 'video') {
    if (!videoUrl) {
      if (mode === 'row') {
        if (posterUrl) {
          return (
            <div className="relative w-11 h-11 rounded border border-white/20 overflow-hidden">
              <img
                src={resolvedPosterUrl}
                alt={item.name || item.media_id || item.id}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={() => handleCandidateError(posterUrl)}
              />
              <span className="absolute bottom-0 left-0 right-0 bg-black/70 text-[9px] uppercase tracking-wide text-zinc-100 text-center">
                Video
              </span>
            </div>
          );
        }

        // Fall back to video element for first-frame thumbnail
        if (resolvedSourceFileUrl) {
          return (
            <div className="relative w-11 h-11 rounded border border-white/20 overflow-hidden bg-black/40">
              <VideoThumbnail src={resolvedSourceFileUrl} className="w-full h-full object-cover" />
            </div>
          );
        }
        return (
          <div className="w-11 h-11 rounded border border-blue-400/40 bg-blue-500/10 flex items-center justify-center text-[9px] uppercase tracking-wide text-blue-200">
            Video
          </div>
        );
      }

      return (
        <div className="space-y-2">
          {syncedToggle}
          {posterUrl && (
            <img
              src={resolvedPosterUrl}
              alt={item.name || item.media_id || item.id}
              className={posterClass}
              loading="lazy"
              onError={() => handleCandidateError(posterUrl)}
            />
          )}
          <p className="attachment-meta">
            Video preview is unavailable for this file in the current browser. Open the file to verify playback.
          </p>
          {resolvedSourceFileUrl && (
            <a
              href={resolvedSourceFileUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center rounded border border-white/25 px-2.5 py-1 text-[11px] text-zinc-100 hover:bg-white/10"
            >
              Open Source File
            </a>
          )}
        </div>
      );
    }

    if (mode === 'row') {
      return (
        <div className="relative w-11 h-11 rounded border border-white/20 overflow-hidden bg-black/40">
          {posterUrl ? (
            <img
              src={resolvedPosterUrl}
              alt={item.name || item.media_id || item.id}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={() => handleCandidateError(posterUrl)}
            />
          ) : resolvedVideoUrl ? (
            <VideoThumbnail src={resolvedVideoUrl} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[9px] uppercase tracking-wide text-blue-200 bg-blue-500/10">
              Video
            </div>
          )}
          <span className="absolute inset-0 flex items-center justify-center bg-black/35 text-[10px] text-white">Play</span>
        </div>
      );
    }
    return (
      <>
        {syncedToggle}
        <video
          key={`${resolvedVideoUrl}#${videoRetries}`}
          ref={videoElRef}
          src={resolvedVideoUrl}
          className={videoClass}
          controls
          playsInline
          preload="metadata"
          poster={resolvedPosterUrl || undefined}
          onError={() => handleVideoError(videoUrl)}
        />
      </>
    );
  }

  if (folder === 'audio') {
    if (!audioUrl) {
      return renderNoPreview(folder, mode, 'Audio preview is unavailable for this file.');
    }

    return mode === 'row' ? (
      <div className="w-11 h-11 rounded border border-amber-300/30 bg-amber-500/10 flex items-center justify-center text-[10px] text-amber-200 uppercase">
        Audio
      </div>
    ) : (
      <audio
        src={resolvedAudioUrl}
        className="w-full"
        controls
        preload="metadata"
        onError={() => handleCandidateError(audioUrl)}
      />
    );
  }

  return mode === 'row' ? (
    <div className={`w-11 h-11 ${basePlaceholder}`}>{folder}</div>
  ) : (
    <p className="attachment-meta">Preview not supported for this media type.</p>
  );
};
